import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import UserProfile from '../index';
import AttachmentDialog from '../Personal/sections/AttachmentDialog';
import DocumentUploadModal from './components/DocumentUploadModal';
import OnDemandDocuments from './components/OnDemandDocuments';
import Pagination from '../../../components/Pagination';
import { UserAttachmentView, UserAttachmentInput, FileType } from '../../../models/general';
import {
  getUserAttachments, uploadAttachment, editAttachment,
  deleteUserAttachment, previewAttachment, downloadAttachment
} from '../../../api/General/attachment';
import { getOcrSettings, OcrSettingsView } from '../../../api/General/ocrSettings';
import { formatDate, formatFileSize } from '../../../utils';
import { RootState } from '../../../store/redux/store/store';
import { parseApiUtc, parseApiDateOnly } from '@/utils/apiDate';

const fileTypeBadge = (ft?: FileType) => {
  const map: Record<string, string> = {
    Image: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    Document: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    Video: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    Audio: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    Other: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  };
  if (!ft) return null;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${map[ft] || map.Other}`}>
      {ft}
    </span>
  );
};

function getExpiryStatus(expiryDate?: string | null, reminderDays?: number | null): 'expired' | 'expiring' | 'valid' | null {
  if (!expiryDate) return null;
  // Parse date components directly to avoid timezone shifting
  const [year, month, day] = expiryDate.substring(0, 10).split('-').map(Number);
  const expiry = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (expiry < today) return 'expired';
  if (reminderDays && reminderDays > 0) {
    const reminderDate = new Date(expiry);
    reminderDate.setDate(reminderDate.getDate() - reminderDays);
    if (today >= reminderDate) return 'expiring';
  }
  return 'valid';
}

function getDaysUntilExpiry(expiryDate: string): number {
  const [year, month, day] = expiryDate.substring(0, 10).split('-').map(Number);
  const expiry = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

const Documents: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const storeUserId = useSelector((state: RootState) => state.user.userId);
  const userId = id ?? storeUserId;
  // On-demand generation always fills the CALLER'S own data, so the section is only
  // meaningful (and the route is only self-scoped) on one's own profile.
  const isOwnProfile = !id || id === storeUserId;

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<UserAttachmentView[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [input, setInput] = useState<UserAttachmentInput>({
    file: null, fileName: '', expiryDate: undefined, reminderDays: 0,
    userId, description: '', companyAttachmentTypeId: undefined,
  });
  const [selected, setSelected] = useState<UserAttachmentView | null>(null);

  // OCR settings
  const [ocrSettings, setOcrSettings] = useState<OcrSettingsView | null>(null);

  // Drag-and-drop state
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);
  const [initialFiles, setInitialFiles] = useState<File[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getUserAttachments(userId);
      setItems(res?.payload || []);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  // Fetch OCR settings on mount
  useEffect(() => {
    const fetchOcrSettings = async () => {
      try {
        const res = await getOcrSettings();
        if (res?.payload) {
          setOcrSettings(res.payload);
        }
      } catch {
        // OCR settings are optional; if the endpoint fails, leave null
      }
    };
    fetchOcrSettings();
  }, []);

  useEffect(() => { load(); }, [userId]);

  // Drag-and-drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragActive(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounter.current = 0;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setInitialFiles(files);
      setAddOpen(true);
    }
  };

  const openAdd = () => {
    setInitialFiles([]);
    setInput({ file: null, fileName: '', expiryDate: undefined, reminderDays: 0, userId, description: '', companyAttachmentTypeId: undefined, documentNumber: '', issueDate: undefined });
    setAddOpen(true);
  };

  const handleUploadSave = async (uploadInput: UserAttachmentInput) => {
    setLoading(true);
    try {
      await uploadAttachment(uploadInput);
      setAddOpen(false);
      setInitialFiles([]);
      await load();
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (a: UserAttachmentView) => {
    setSelected(a);
    setInput({ file: null, fileName: a.fileName, expiryDate: a.expiryDate, reminderDays: a.reminderDays, userId, description: a.fileDescription, companyAttachmentTypeId: a.companyAttachmentTypeId, documentNumber: a.documentNumber, issueDate: a.issueDate });
    setEditOpen(true);
  };
  const edit = async () => { setLoading(true); await editAttachment(selected?.id || 0, input); setEditOpen(false); await load(); setLoading(false); };
  const openDelete = (a: UserAttachmentView) => { setSelected(a); setDeleteOpen(true); };
  const del = async () => { setLoading(true); if (selected) await deleteUserAttachment(selected.id); setDeleteOpen(false); await load(); setLoading(false); };

  const view = async (docId: number) => {
    const blob = await previewAttachment(docId);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const download = async (doc: UserAttachmentView) => {
    const blob = await downloadAttachment(doc.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.fileName || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const paged = items.slice((page - 1) * pageSize, page * pageSize);

  return (
    <UserProfile>
      <div
        className="relative p-4 sm:p-6"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{t('DOCUMENTS')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t('Manage employee documents and attachments')}</p>
          </div>
          <button
            onClick={openAdd}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            {t('Add Document')}
          </button>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('File Name')}</th>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('Description')}</th>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('Attachment Type')}</th>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('documentNumber')}</th>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('File Type')}</th>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('Size')}</th>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('Upload Date')}</th>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('issueDate')}</th>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('Expiry Date')}</th>
                  <th className="px-4 py-3 text-start text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('Reminder Days')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">{t('Actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-card">
                {loading && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {t('No documents found')}
                    </td>
                  </tr>
                )}
                {!loading && paged.map((a) => (
                  <tr key={a.id} className="hover:bg-muted transition-colors">
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                      {a.fileName ? a.fileName : <span className="italic text-muted-foreground">{t('noFileAttached')}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-[200px] truncate">{a.fileDescription}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">{a.companyAttachmentType?.attachmentType}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">{a.documentNumber}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">{a.filePath ? fileTypeBadge(a.fileType) : <span className="text-muted-foreground">&mdash;</span>}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">{a.filePath ? formatFileSize(a.fileSize) : <span>&mdash;</span>}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">{a.createdOn ? formatDate((parseApiUtc(a.createdOn) ?? new Date(NaN))) : ''}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">{a.issueDate ? formatDate((parseApiDateOnly(a.issueDate) ?? new Date(NaN))) : ''}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                      {a.expiryDate ? formatDate((parseApiDateOnly(a.expiryDate) ?? new Date(NaN))) : ''}
                      {(() => {
                        const status = getExpiryStatus(a.expiryDate, a.reminderDays);
                        if (status === 'expired') {
                          return (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400 rtl:ml-0 rtl:mr-2 ltr:ml-2">
                              {t('documents.expired')}
                            </span>
                          );
                        }
                        if (status === 'expiring') {
                          const days = getDaysUntilExpiry(a.expiryDate!);
                          return (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rtl:ml-0 rtl:mr-2 ltr:ml-2">
                              {t('documents.expiresInDays', { count: days })}
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">{a.reminderDays}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-center">
                      <div className="inline-flex items-center gap-1">
                        {a.filePath && (
                          <button onClick={() => view(a.id)} className="rounded p-1.5 text-muted-foreground hover:bg-blue-50 dark:hover:bg-blue-950/30 hover:text-blue-600 dark:hover:text-blue-400" aria-label={t('View') as string} title={t('View') as string}>
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                        )}
                        {a.filePath && (
                          <button onClick={() => download(a)} className="rounded p-1.5 text-muted-foreground hover:bg-green-50 dark:hover:bg-green-950/30 hover:text-green-600 dark:hover:text-green-400" aria-label={t('Download') as string} title={t('Download') as string}>
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                          </button>
                        )}
                        <button onClick={() => openEdit(a)} className="rounded p-1.5 text-muted-foreground hover:bg-amber-50 dark:hover:bg-amber-950/30 hover:text-amber-600 dark:hover:text-amber-400" aria-label={t('Edit') as string} title={t('Edit') as string}>
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                          </svg>
                        </button>
                        <button onClick={() => openDelete(a)} className="rounded p-1.5 text-muted-foreground hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400" aria-label={t('Delete') as string} title={t('Delete') as string}>
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && items.length > pageSize && (
          <div className="mt-4">
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(items.length / pageSize)}
              onPageChange={setPage}
            />
          </div>
        )}

        {/* On-demand HR documents (own profile only — generation is self-scoped) */}
        {isOwnProfile && <OnDemandDocuments />}

        {/* Drag-and-drop overlay */}
        {dragActive && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-primary bg-background p-12 shadow-2xl">
              <svg className="h-16 w-16 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <p className="text-lg font-medium text-foreground">{t('Drop files to upload')}</p>
              <p className="text-sm text-muted-foreground">{t('PDF, JPG, PNG')}</p>
            </div>
          </div>
        )}

        {/* Dialogs */}
        <DocumentUploadModal
          open={addOpen}
          onClose={() => { setAddOpen(false); setInitialFiles([]); }}
          onSave={async (uploadInput, _additionalFiles) => { await handleUploadSave(uploadInput); }}
          loading={loading}
          title={t('Add Document')}
          userId={userId}
          ocrSettings={ocrSettings}
          initialFiles={initialFiles.length > 0 ? initialFiles : undefined}
        />
        <AttachmentDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSave={edit}
          attachmentInput={input}
          setAttachmentInput={setInput}
          loading={loading}
          title={t('Edit Attachment')}
        />
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{t('Delete Attachment')}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">{t('Are you sure you want to delete this attachment?')}</p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('Cancel')}</Button>
              <Button variant="destructive" onClick={del}>
                {loading ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : t('Delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </UserProfile>
  );
};

export default Documents;
